import React, { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  getManagedLeadLifecycle,
  ManagedLeadCategory,
  ManagedLeadListParams,
  ManagedLeadListResponse,
  ManagedLeadProductPath,
  ManagedLeadsUpdateRequestFilters,
} from '../../../services/api/managed-leads/managedLeads';
import useManagedLeadListParams from '../hooks/useManagedLeadListParams';
import {
  MANAGED_LEAD_QUERY_OPTIONS,
  managedLeadLifecycleQueryKey,
  managedLeadListQueryKey,
} from '../managedLeadQueryKeys';
import ManagedLeadListPageContent from './ManagedLeadListPageContent';

const PAGE_SIZE = 25 as const;

interface ManagedProductLeadListPageProps<T> {
  category: ManagedLeadCategory;
  product: ManagedLeadProductPath;
  listLeads: (
    params: ManagedLeadListParams
  ) => Promise<ManagedLeadListResponse<T>>;
  loadingMessage: string;
  ownerMissingMessage: string;
  searchLabel: string;
  renderLead: (
    lead: T,
    context: {
      returnTo: string;
      showNeedsUpdate: boolean;
      /** In gestione only: Vinte and Drop offer no Richiesta di aggiornamento. */
      showUpdateRequest: boolean;
    }
  ) => ReactNode;
}

const ManagedProductLeadListPage = <T,>({
  category,
  product,
  listLeads,
  loadingMessage,
  ownerMissingMessage,
  searchLabel,
  renderLead,
}: ManagedProductLeadListPageProps<T>) => {
  const params = useManagedLeadListParams(category);
  const listParams: ManagedLeadListParams = {
    category,
    page: params.page,
    limit: PAGE_SIZE,
    ...(params.search.length >= 2 ? { search: params.search } : {}),
    ...(category === 'in_progress' && params.filters.stages.length
      ? { stages: params.filters.stages.join(',') }
      : {}),
    ...(category === 'in_progress' && params.filters.needsUpdate
      ? { needsUpdate: true }
      : {}),
  };
  // The aggregate Richiesta di aggiornamento reruns this very selection on the
  // server, so it carries the list filters and nothing else.
  const updateRequestFilters: ManagedLeadsUpdateRequestFilters = {
    ...(listParams.search ? { search: listParams.search } : {}),
    ...(listParams.stages ? { stages: listParams.stages } : {}),
    ...(listParams.needsUpdate ? { needsUpdate: true } : {}),
  };
  const query = useQuery({
    ...MANAGED_LEAD_QUERY_OPTIONS,
    queryKey: [...managedLeadListQueryKey(product), listParams],
    queryFn: () => listLeads(listParams),
  });
  const lifecycleQuery = useQuery({
    ...MANAGED_LEAD_QUERY_OPTIONS,
    queryKey: managedLeadLifecycleQueryKey(product),
    queryFn: () => getManagedLeadLifecycle(product),
    enabled: category === 'in_progress',
  });

  return (
    <ManagedLeadListPageContent
      product={product}
      loadingMessage={loadingMessage}
      ownerMissingMessage={ownerMissingMessage}
      category={category}
      searchLabel={searchLabel}
      hasActiveSearch={params.search.length >= 2}
      searchInput={params.searchInput}
      setSearchInput={params.setSearchInput}
      filters={params.filters}
      applyFilters={params.applyFilters}
      lifecycleQuery={lifecycleQuery}
      page={params.page}
      setPage={params.setPage}
      query={query}
      updateRequestFilters={
        category === 'in_progress' ? updateRequestFilters : undefined
      }
      renderLead={(lead) =>
        renderLead(lead, {
          returnTo: params.returnTo,
          showNeedsUpdate: category === 'in_progress',
          showUpdateRequest: category === 'in_progress',
        })
      }
    />
  );
};

export default ManagedProductLeadListPage;
